import MaterialTable from '@material-table/core'
import ClearSharpIcon from '@mui/icons-material/ClearSharp'
import moment from 'moment'
import { BaseSyntheticEvent, FC, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { ChargeException } from '../../../../api/models/ChargeException.type'
import { Supplier } from '../../../../api/supplier/suppliers.types'
import { suppliersAPI } from '../../../../api/supplier/suppliersAPI'

interface OverridesGridProps {
  shouldRefetchData: number
}
const isValidOrZero = (value: number | undefined) => {
  return value || value === 0
}
const buildDetailDisplayString = (exception: ChargeException) => {
  let fromDate
  let toDate

  if (exception.customExceptionTypeName === 'DateTime') {
    //There is a date to be displayed
    fromDate = `${moment(exception.appliesFromDate).format(
      'DD-MM-YYYY',
    )} ${exception.appliesFromTimeHour?.toLocaleString('en', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    })}:${exception.appliesFromTimeMinute?.toLocaleString('en', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    })}`
    toDate = `${moment(exception.appliesToDate).format(
      'DD-MM-YYYY',
    )} ${exception.appliesToTimeHour?.toLocaleString('en', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    })}:${exception.appliesToTimeMinute?.toLocaleString('en', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    })}`
  }
  if (exception.customExceptionTypeName === 'Date') {
    //There is a date to be displayed
    fromDate = `${moment(exception.appliesFromDate).format('DD-MM-YYYY')}`
    toDate = `${moment(exception.appliesToDate).format('DD-MM-YYYY')} `
  }
  if (
    exception.customExceptionTypeName === 'Time' ||
    exception.customExceptionTypeName === 'Day' ||
    exception.customExceptionTypeName === 'Weekday' ||
    exception.customExceptionTypeName === 'Weekend'
  ) {
    //There is a date to be displayed
    if (
      isValidOrZero(exception.appliesFromTimeHour) &&
      isValidOrZero(exception.appliesFromTimeMinute)
    ) {
      fromDate = `${exception.appliesFromTimeHour?.toLocaleString('en', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })}:${exception.appliesFromTimeMinute?.toLocaleString('en', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })}`
    }
    if (
      isValidOrZero(exception.appliesToTimeHour) &&
      isValidOrZero(exception.appliesToTimeMinute)
    ) {
      toDate = `${exception.appliesToTimeHour?.toLocaleString('en', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })}:${exception.appliesToTimeMinute?.toLocaleString('en', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })}`
    }
  }
  let detail = ''
  if (exception.customExceptionTypeName === 'Day')
    detail += `Day: ${exception.appliesOnDay} `
  if (exception.customExceptionTypeName === 'Weekday') detail += `All Weekdays `
  if (exception.customExceptionTypeName === 'Weekend') detail += `All Weekend `
  if (fromDate) detail += `From: ${fromDate} `
  if (toDate) detail += `To: ${toDate}`

  return detail
}
const OverridesGrid: FC<OverridesGridProps> = (props) => {
  const { shouldRefetchData } = props
  const supplier: Supplier = useSelector(
    (state: any) => state.suppliers.supplier,
  )
  const supplierOverrides = useQuery({
    queryKey: ['getChargeExceptions', supplier.id],
    queryFn: async () => suppliersAPI.getChargeExceptions(supplier.id),
    refetchOnWindowFocus: false,
  })
  const { mutateAsync: deleteChargeMutate } = useMutation({
    mutationFn: (chargeRemoval: any) =>
      suppliersAPI.deleteChargeException(
        chargeRemoval.supplierId,
        chargeRemoval.chargeException,
      ),
  })

  useEffect(() => {
    if (shouldRefetchData > 0) {
      supplierOverrides.refetch()
    }
  }, [shouldRefetchData]) // Remove supplierOverrides from dependencies

  if (supplierOverrides.error) {
    //TODO:make an error component
    return <div>{JSON.stringify(supplierOverrides.error)}</div>
  }
  const exceptions = supplierOverrides.data?.data.value

  const mapExceptionsToArray = (exception: ChargeException) => {
    const displayString = buildDetailDisplayString(exception)
    const data = {
      id: exception.id,
      name: exception.name,
      type: exception.customExceptionTypeName,
      detail: displayString,
      isDefaultSelection: exception.isDefault,
    }
    return data
  }

  const tableData = () => {
    const tblData: Array<object> = []
    if (exceptions) {
      exceptions.forEach((exception) => {
        const mapped = mapExceptionsToArray(exception)
        tblData.push(mapped)
      })
    }
    return tblData
  }

  const handleDeleteChargeExceptionClick = (
    event: BaseSyntheticEvent,
    chargeException: number,
  ) => {
    if (chargeException) {
      event.preventDefault()
      const chargeRemoval = {
        supplierId: supplier.id,
        chargeException: chargeException,
      }
      deleteChargeMutate(chargeRemoval).then(() => {
        supplierOverrides.refetch()
      })
    }
  }

  return (
    <MaterialTable
      title="Charge Overrides"
      columns={[
        { title: 'id', field: 'id', hidden: true },
        {
          title: 'Name',
          field: 'name',
          cellStyle: { width: 200, maxWidth: 500 },
          headerStyle: { width: 200, maxWidth: 500 },
        },
        {
          title: 'Type',
          field: 'type',
          lookup: {
            DateTime: 'Date & Time',
            Date: 'Date',
            Time: 'Time',
            Always: 'Always',
            Day: 'Specific Day',
            Weekday: 'Weekday',
            Weekend: 'Weekend',
          },
        },
        { title: 'Detail', field: 'detail' },
        { title: 'Default', field: 'isDefaultSelection', hidden: true },
      ]}
      data={tableData()}
      options={{
        headerStyle: {
          backgroundColor: '#30527e',
          color: '#FFF',
          padding: '16px',
        },
        rowStyle: {
          backgroundColor: '#ffffff',
          fontSize: 'var(--fs-12, 0.75rem)',
        },
        grouping: true,
        emptyRowsWhenPaging: false,
        pageSize: 10,
        pageSizeOptions: [10, 50, 100],
        actionsColumnIndex: -1,
      }}
      localization={{
        pagination: {
          labelRowsPerPage: '',
        },
      }}
      style={{
        backgroundColor: '#ededed',
        zIndex: 0,
      }}
      isLoading={supplierOverrides.isLoading}
      actions={[
        {
          // eslint-disable-next-line react/display-name
          icon: () => <ClearSharpIcon fontSize="small" color="error" />,
          tooltip: 'Delete Override',
          onClick: (event: BaseSyntheticEvent, rowData: any) =>
            handleDeleteChargeExceptionClick(event, rowData.id),
        },
      ]}
    />
  )
}

export default OverridesGrid
